/*!
 * @license
 * Copyright Squiz Australia Pty Ltd. All Rights Reserved.
 */

import { NotEmptyValidator } from '@squiz/dxp-console-support';
import {
  Button,
  BUTTON_FORMAT_SOFT,
  BUTTON_FORMAT_STRONG,
  BUTTON_THEME_DEFAULT,
  BUTTON_THEME_PRIMARY,
  BUTTON_THEME_SUCCESS,
  ButtonProps,
  Form,
  FORM_THEME_LITE,
  FormProps,
  ICON_DATA_OBJECT,
  ICON_LEAK_ADD,
  ICON_TOGGLE_ON,
  MultiChipField,
  MultiChipFieldOnItemsChangeProps,
  MultiChipFieldProps,
  SdsHeading,
  SdsIcon,
  SdsText,
  SdsTextSize,
  SdsTextWeight,
  SelectField,
  SelectFieldProps,
  TextField,
  TextFieldProps,
  Toggle,
  ToggleProps,
} from '@squiz/sds';
import React, { ChangeEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { screenPaths } from '../../../../Constants/ScreenPaths.constants';
import { CONFIGURATION_STRINGS } from '../../../../Constants/Strings.constants';
import { DAM_INTEGRATIONS_DATA_IDS, DAM_INTEGRATIONS_FORM_IDS } from './AddDamIntegrationDetails.constants';
import { damIntegrationsSelectOptions, defaultChips } from './AddDamIntegrationDetails.fixtures';

export const AddDamIntegrationDetails = (): JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [fieldErrors, setFieldErrors] = useState<Record<string, string | null>>({});
  const [submit] = useState<boolean>(false);
  const [isDisabled, setIsDisabled] = useState<boolean>(true);
  const [formValues, setFormValues] = useState({
    damIntegrationsAccessTokenUrl: '',
    damIntegrationsAuthUrl: '',
    damIntegrationsClientId: '',
    damIntegrationsClientSecret: '',
    damIntegrationsCopyUrl: 'https://{MATRIX}/__internal-api/v1/dam-integration/auth/{INTEGRATION_UUID}',
    damIntegrationsDamDomain: '',
    damIntegrationsEnabled: false,
    damIntegrationsM2MClientId: '',
    damIntegrationsM2MClientSecret: '',
    damIntegrationsName: '',
    damIntegrationsScope: defaultChips.map((chip) => chip.text).join(';'),
    damIntegrationsType: '',
  });

  const fieldRefs = useRef({
    damIntegrationsAccessTokenUrl: useRef<HTMLInputElement>(null),
    damIntegrationsAdd: useRef<HTMLButtonElement>(null),
    damIntegrationsAuthUrl: useRef<HTMLInputElement>(null),
    damIntegrationsCancel: useRef<HTMLButtonElement>(null),
    damIntegrationsCheckConnection: useRef<HTMLButtonElement>(null),
    damIntegrationsClientId: useRef<HTMLInputElement>(null),
    damIntegrationsClientSecret: useRef<HTMLInputElement>(null),
    damIntegrationsCopyUrl: useRef<HTMLInputElement>(null),
    damIntegrationsDamDomain: useRef<HTMLInputElement>(null),
    damIntegrationsEnabled: useRef<HTMLButtonElement>(null),
    damIntegrationsM2MClientId: useRef<HTMLInputElement>(null),
    damIntegrationsM2MClientSecret: useRef<HTMLInputElement>(null),
    damIntegrationsName: useRef<HTMLInputElement>(null),
    damIntegrationsScope: useRef<HTMLInputElement>(null),
    damIntegrationsType: useRef<HTMLInputElement>(null),
  });

  const labels = useMemo(
    () => ({
      cancel: t(CONFIGURATION_STRINGS.cancel),
      damIntegrationsAccessTokenUrl: t(CONFIGURATION_STRINGS.damIntegrationsAccessTokenUrl),
      damIntegrationsAccessTokenUrlDescription: t(CONFIGURATION_STRINGS.damIntegrationsAccessTokenUrlDescription),
      damIntegrationsAuthRedirectURI: t(CONFIGURATION_STRINGS.damIntegrationsAuthRedirectURI),
      damIntegrationsAuthRedirectURISection: t(CONFIGURATION_STRINGS.damIntegrationsAuthRedirectURISection),
      damIntegrationsAuthUrl: t(CONFIGURATION_STRINGS.damIntegrationsAuthUrl),
      damIntegrationsAuthUrlDescription: t(CONFIGURATION_STRINGS.damIntegrationsAuthUrlDescription),
      damIntegrationsClientId: t(CONFIGURATION_STRINGS.damIntegrationsClientId),
      damIntegrationsClientIdDescription: t(CONFIGURATION_STRINGS.damIntegrationsClientIdDescription),
      damIntegrationsClientSecret: t(CONFIGURATION_STRINGS.damIntegrationsClientSecret),
      damIntegrationsClientSecretDescription: t(CONFIGURATION_STRINGS.damIntegrationsClientSecretDescription),
      damIntegrationsConfigurationSection: t(CONFIGURATION_STRINGS.damIntegrationsConfigurationSection),
      damIntegrationsConnectionTest: t(CONFIGURATION_STRINGS.damIntegrationsConnectionTest),
      damIntegrationsConnectionTestDescription: t(CONFIGURATION_STRINGS.damIntegrationsConnectionTestDescription),
      damIntegrationsCopyUrl: t(CONFIGURATION_STRINGS.damIntegrationsCopyUrl),
      damIntegrationsCopyUrlDescription: t(CONFIGURATION_STRINGS.damIntegrationsCopyUrlDescription),
      damIntegrationsDamDomain: t(CONFIGURATION_STRINGS.damIntegrationsDamDomain),
      damIntegrationsDamDomainDescription: t(CONFIGURATION_STRINGS.damIntegrationsDamDomainDescription),
      damIntegrationsDetailsSection: t(CONFIGURATION_STRINGS.damIntegrationsDetailsSection),
      damIntegrationsEnabled: t(CONFIGURATION_STRINGS.damIntegrationsEnabled),
      damIntegrationsEnabledDescription: t(CONFIGURATION_STRINGS.damIntegrationsAccessTokenUrlDescription),
      damIntegrationsEnabledSection: t(CONFIGURATION_STRINGS.damIntegrationsEnabledSection),
      damIntegrationsM2MClientId: t(CONFIGURATION_STRINGS.damIntegrationsM2MClientId),
      damIntegrationsM2MClientIdDescription: t(CONFIGURATION_STRINGS.damIntegrationsM2MClientIdDescription),
      damIntegrationsM2MClientSecret: t(CONFIGURATION_STRINGS.damIntegrationsM2MClientSecret),
      damIntegrationsM2MClientSecretDescription: t(CONFIGURATION_STRINGS.damIntegrationsM2MClientSecretDescription),
      damIntegrationsName: t(CONFIGURATION_STRINGS.damIntegrationsName),
      damIntegrationsNameDescription: t(CONFIGURATION_STRINGS.damIntegrationsNameDescription),
      damIntegrationsRenderSection: t(CONFIGURATION_STRINGS.damIntegrationsRenderSection),
      damIntegrationsScope: t(CONFIGURATION_STRINGS.damIntegrationsScope),
      damIntegrationsScopeDescription: t(CONFIGURATION_STRINGS.damIntegrationsScopeDescription),
      damIntegrationsScopeInvalidFormat: t(CONFIGURATION_STRINGS.damIntegrationsScopeInvalidFormat),
      damIntegrationsType: t(CONFIGURATION_STRINGS.damIntegrationsType),
      damIntegrationsTypeDescription: t(CONFIGURATION_STRINGS.damIntegrationsTypeDescription),
      invalidUrlError: t(CONFIGURATION_STRINGS.invalidUrlError),
      requiredField: t(CONFIGURATION_STRINGS.requiredField),
      save: t(CONFIGURATION_STRINGS.save),
    }),
    [t],
  );

  const notEmptyValidator = useMemo(() => new NotEmptyValidator<boolean>(() => false), []);

  const isValidUrl = (url: string): boolean => {
    try {
      // eslint-disable-next-line no-new
      new URL(url);
      return true;
    } catch (_) {
      return false;
    }
  };

  const validateChipInput = (text: string): boolean => {
    const scopePattern = /^[a-zA-Z0-9._-]+:(write|read)|(offline)/;
    const isValid = scopePattern.test(text);
    return isValid;
  };

  const validateFields = useCallback(
    (inputs: Array<HTMLInputElement>) => {
      const nextErrors = { ...fieldErrors };

      inputs.forEach((input) => {
        const inputValue = input.value;
        const inputFieldId = input.id as keyof typeof DAM_INTEGRATIONS_FORM_IDS;
        if (!notEmptyValidator.validate(inputValue)) {
          nextErrors[inputFieldId] = labels.requiredField;
        } else {
          if (
            (inputFieldId === DAM_INTEGRATIONS_FORM_IDS.damIntegrationsAuthUrl ||
              inputFieldId === DAM_INTEGRATIONS_FORM_IDS.damIntegrationsAccessTokenUrl ||
              inputFieldId === DAM_INTEGRATIONS_FORM_IDS.damIntegrationsDamDomain) &&
            !isValidUrl(inputValue)
          ) {
            nextErrors[inputFieldId] = labels.invalidUrlError;
          } else {
            delete nextErrors[inputFieldId];
          }
        }
      });
      setFieldErrors(nextErrors);
    },
    [fieldErrors, labels, notEmptyValidator],
  );

  const handleInputChange = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const { name, value } = event.target;
      setFormValues((prevValues) => ({
        ...prevValues,
        [name]: value,
      }));
      validateFields([event.target]);
    },
    [validateFields],
  );

  const onScopeChangeHandler = useCallback(
    (fieldId: TextFieldId) =>
      ({ valid, items }: MultiChipFieldOnItemsChangeProps): void => {
        const nextErrors = { ...fieldErrors };
        if (items.length === 0) {
          nextErrors[DAM_INTEGRATIONS_FORM_IDS[fieldId]] = labels.requiredField;
        } else if (!valid) {
          nextErrors[DAM_INTEGRATIONS_FORM_IDS[fieldId]] = labels.damIntegrationsScopeInvalidFormat;
        } else {
          delete nextErrors[DAM_INTEGRATIONS_FORM_IDS[fieldId]];
        }
        setFieldErrors(nextErrors);
        setFormValues((prevValues) => ({
          ...prevValues,
          damIntegrationsScope: items.join(';'),
        }));
      },
    [fieldErrors, labels, setFieldErrors, setFormValues],
  );

  const checkConnectionHandler = useCallback(() => {
    // TODO handle enabled switch here, probably patch to tenant settings
  }, []);

  const addIntegrationHandler = useCallback(() => {
    // TODO handle posting formValues here
  }, []);

  const cancelActionHandler = useCallback(() => {
    navigate({ pathname: screenPaths.damIntegrations });
  }, [navigate]);

  useEffect(() => {
    const isFormValid = Object.entries(formValues).every(([key, value]) => {
      if (key === 'damIntegrationsEnabled') {
        return true;
      }
      return notEmptyValidator.validate(value);
    });

    setIsDisabled(!(isFormValid && Object.keys(fieldErrors).length === 0));
  }, [fieldErrors, formValues, notEmptyValidator]);

  type TextFieldId = Exclude<
    keyof typeof DAM_INTEGRATIONS_FORM_IDS,
    'damIntegrationsEnabled' | 'damIntegrationsAdd' | 'damIntegrationsCancel' | 'damIntegrationsCheckConnection'
  >;

  const getTextFieldProps = (fieldId: TextFieldId): TextFieldProps => ({
    dataId: DAM_INTEGRATIONS_DATA_IDS[fieldId],
    errorMessage: fieldErrors[DAM_INTEGRATIONS_FORM_IDS[fieldId]] || null,
    fieldLabel: labels[fieldId],
    id: DAM_INTEGRATIONS_FORM_IDS[fieldId],
    name: fieldId,
    note: !fieldErrors[DAM_INTEGRATIONS_FORM_IDS[fieldId]] ? labels[`${fieldId}Description`] : undefined,
    onChange: handleInputChange,
    required: true,
    value: formValues[fieldId],
  });

  const getButtonProps = (fieldId: keyof typeof DAM_INTEGRATIONS_FORM_IDS): ButtonProps => ({
    dataId: DAM_INTEGRATIONS_DATA_IDS[fieldId],
    format: BUTTON_FORMAT_STRONG,
    id: DAM_INTEGRATIONS_FORM_IDS[fieldId],
    isLoading: submit,
    text: labels.save,
    theme: BUTTON_THEME_PRIMARY,
  });

  const getScopeFieldProps = (fieldId: TextFieldId): MultiChipFieldProps => ({
    ...getTextFieldProps(fieldId),
    callback: validateChipInput,
    defaultChips,
    onItemsChange: onScopeChangeHandler(fieldId),
  });

  const getIntegrationTypeFieldProps = (fieldId: TextFieldId): SelectFieldProps => {
    const { dataId, ...textFieldProps } = getTextFieldProps(fieldId);
    return {
      ...textFieldProps,
      callback: (option): void => {
        setFormValues((prevValues) => ({
          ...prevValues,
          [fieldId]: option.key,
        }));
      },
      options: damIntegrationsSelectOptions,
    };
  };

  const addButtonProps: ButtonProps = {
    ...getButtonProps('damIntegrationsAdd'),
    disabled: isDisabled,
    onClick: addIntegrationHandler,
    text: labels.save,
    theme: BUTTON_THEME_SUCCESS,
  };

  const cancelButtonProps: ButtonProps = {
    ...getButtonProps('damIntegrationsCancel'),
    format: BUTTON_FORMAT_SOFT,
    isLoading: false,
    onClick: cancelActionHandler,
    text: labels.cancel,
    theme: BUTTON_THEME_DEFAULT,
  };

  const checkConnectionButtonProps: ButtonProps = {
    ...getButtonProps('damIntegrationsCheckConnection'),
    format: BUTTON_FORMAT_SOFT,
    icon: ICON_LEAK_ADD,
    onClick: checkConnectionHandler,
    text: labels.damIntegrationsConnectionTest,
    theme: BUTTON_THEME_DEFAULT,
  };

  const enableIntegrationProps: ToggleProps = {
    checked: formValues.damIntegrationsEnabled,
    disabled: isDisabled,
    fieldLabel: labels.damIntegrationsEnabled,
    id: DAM_INTEGRATIONS_FORM_IDS.damIntegrationsEnabled,
    name: DAM_INTEGRATIONS_FORM_IDS.damIntegrationsEnabled,
  };

  const formProps = {
    theme: FORM_THEME_LITE,
  } as FormProps;

  return (
    <div>
      <div className="dam-integrations__container">
        <div className="dam-integrations__form">
          <Form {...formProps}>
            <div className="dam-integrations__form-section">
              <div className="dam-integrations__section-heading">
                <SdsHeading level={4} text={labels.damIntegrationsDetailsSection} />
              </div>
              <TextField {...getTextFieldProps('damIntegrationsName')} ref={fieldRefs.current.damIntegrationsName} />
              <SelectField
                {...getIntegrationTypeFieldProps('damIntegrationsType')}
                data-id={DAM_INTEGRATIONS_DATA_IDS.damIntegrationsType}
                ref={fieldRefs.current.damIntegrationsType}
              />
            </div>

            <div className="dam-integrations__form-section">
              <legend className="dam-integrations__section-heading">
                <SdsHeading level={4} text={labels.damIntegrationsRenderSection} />
              </legend>
              <TextField
                {...getTextFieldProps('damIntegrationsAuthUrl')}
                ref={fieldRefs.current.damIntegrationsAuthUrl}
              />
              <TextField
                {...getTextFieldProps('damIntegrationsClientId')}
                ref={fieldRefs.current.damIntegrationsClientId}
              />
              <TextField
                {...getTextFieldProps('damIntegrationsClientSecret')}
                ref={fieldRefs.current.damIntegrationsClientSecret}
              />
              <div className="dam-integrations__field-container">
                <SdsText
                  customTag={'div'}
                  fontSize={SdsTextSize.Small}
                  fontWeight={SdsTextWeight.Regular}
                  text={labels.damIntegrationsConnectionTestDescription}
                />
                <Button
                  {...checkConnectionButtonProps}
                  classes={'dam-integrations__connection-button'}
                  key={'check-connection'}
                  ref={fieldRefs.current.damIntegrationsCheckConnection}
                />
              </div>
            </div>
            <div className="dam-integrations__form-section">
              <div className="dam-integrations__section-heading">
                <SdsHeading level={4} text={labels.damIntegrationsConfigurationSection} />
              </div>
              <TextField
                {...getTextFieldProps('damIntegrationsDamDomain')}
                ref={fieldRefs.current.damIntegrationsDamDomain}
              />
              <MultiChipField
                {...getScopeFieldProps('damIntegrationsScope')}
                ref={fieldRefs.current.damIntegrationsScope}
              />
              <TextField
                {...getTextFieldProps('damIntegrationsAccessTokenUrl')}
                ref={fieldRefs.current.damIntegrationsAccessTokenUrl}
              />
              <TextField
                {...getTextFieldProps('damIntegrationsM2MClientId')}
                ref={fieldRefs.current.damIntegrationsM2MClientId}
              />
              <TextField
                {...getTextFieldProps('damIntegrationsM2MClientSecret')}
                ref={fieldRefs.current.damIntegrationsM2MClientSecret}
              />
            </div>
          </Form>
        </div>
        <div className="dam-integrations__form">
          <Form legend={labels.damIntegrationsEnabled} theme={FORM_THEME_LITE}>
            <div className="dam-integrations__form-section">
              <div className="dam-integrations__section-heading--with-icon">
                <SdsIcon svg={ICON_TOGGLE_ON}></SdsIcon>
                <SdsHeading level={4} text={labels.damIntegrationsEnabledSection} />
              </div>
              <SdsText fontWeight={SdsTextWeight.Regular} text={labels.damIntegrationsEnabledDescription} />
              <Toggle {...enableIntegrationProps} ref={fieldRefs.current.damIntegrationsEnabled} />
            </div>

            <div className="dam-integrations__form-section">
              <div className="dam-integrations__section-heading--with-icon">
                <SdsIcon svg={ICON_DATA_OBJECT}></SdsIcon>
                <SdsHeading level={4} text={labels.damIntegrationsAuthRedirectURISection} />
              </div>
              <SdsText
                fontSize={SdsTextSize.Small}
                fontWeight={SdsTextWeight.Regular}
                text={labels.damIntegrationsAuthRedirectURI}
              />
              <div className="dam-integrations__field-container">
                <TextField
                  {...getTextFieldProps('damIntegrationsCopyUrl')}
                  classes={'dam-integrations__field--borderless'}
                  ref={fieldRefs.current.damIntegrationsCopyUrl}
                  showCopyIcon
                />
              </div>
            </div>
          </Form>
        </div>
      </div>
      <div className="dam-integrations__form-control">
        <Button {...cancelButtonProps} key={'cancel-dam'} ref={fieldRefs.current.damIntegrationsCancel} />
        <Button {...addButtonProps} key={'add-dam'} ref={fieldRefs.current.damIntegrationsAdd} />
      </div>
    </div>
  );
};
